import React, { useContext } from 'react'

import { useParams } from 'react-router-dom'

import { DuplicateParticipantSelectionContext } from 'App'
import DuplicatedContactInfoScreen from 'components/DuplicatedContactInfoScreen'
import ErrorState from 'components/ErrorState'
import Loader from 'components/lib/Loader'
import { PublicQuestions } from 'components/Survey/Survey'
import SurveyRouter from 'components/SurveyRouter'
import ReactGA from 'config/googleAnalytics'
import {
  SurveyByParticipantIdQuery,
  useSurveyByParticipantIdQuery,
  ParticipantSourceTypeInput,
  SurveyProductTypeEnum,
} from 'generated/graphql'
import { TranslationKey, useTranslations } from 'locales'
import { LOCTypeEnum } from 'utils/generatedFrontendConstants'

const setAnalyticsDimensions = (
  participantId: string,
  analyticsIdentifiers: SurveyByParticipantIdQuery['participantAnalyticsIdentifiers'],
  productType: SurveyProductTypeEnum,
  linkSource?: string,
) => {
  if (ReactGA && analyticsIdentifiers) {
    ReactGA.set({
      userId: participantId,
      dimension1: analyticsIdentifiers.isTestAccount,
      dimension2: analyticsIdentifiers.orgName,
      dimension3:
        productType === SurveyProductTypeEnum.RESIDENT
          ? analyticsIdentifiers.residentSolutionName
          : analyticsIdentifiers.solutionName,
      dimension4: analyticsIdentifiers.birthYearRange,
      dimension5: analyticsIdentifiers.levelOfCare,
      dimension6: analyticsIdentifiers.residentRespondentType,
      // Because of the redirect, this will be undefined except for initial load. That is okay since it's a session variable in GA
      dimension7: linkSource || '',
    })
  }
}

const SurveyContainer: React.FC = () => {
  const { t } = useTranslations()
  const routeParams = useParams<{
    participantId: string
    sourceFromUrl: ParticipantSourceTypeInput
  }>()
  let participantId = routeParams.participantId
  // Check if the user has selected for a specific participant among duplicated contact info.
  const { duplicateParticipantSelectionId } = useContext(DuplicateParticipantSelectionContext)
  if (duplicateParticipantSelectionId) {
    participantId = duplicateParticipantSelectionId
  }
  const sourceFromUrl = routeParams.sourceFromUrl

  let source: ParticipantSourceTypeInput = ParticipantSourceTypeInput.S
  if (
    sourceFromUrl &&
    Object.values(ParticipantSourceTypeInput).includes(sourceFromUrl as ParticipantSourceTypeInput)
  ) {
    source = sourceFromUrl as ParticipantSourceTypeInput
  }

  const result = useSurveyByParticipantIdQuery({
    variables: { participantId: participantId || '', source },
    skip: !participantId,
  })
  if (result.loading) {
    return <Loader />
  }
  const survey = result?.data?.surveyByParticipantId
  if (!(participantId && survey)) {
    return <ErrorState message={t('Error: This survey code was not found.')} />
  }
  const participant = survey.participant
  if (!participant.location) {
    return <ErrorState message={t('Error: Location is missing for this participant.')} />
  }
  // If requested participant has duplicated contact info and the user has not yet seen it,
  // Show a duplicated contact info screen to allow them to choose which participant they are.
  // Also, bypass duplicate selection if the user entered their survey code from the Portal screen.
  if (
    participant.duplicatedContactInfo?.length &&
    !duplicateParticipantSelectionId &&
    source !== ParticipantSourceTypeInput.L
  ) {
    return (
      <DuplicatedContactInfoScreen
        duplicatedContactInfo={participant.duplicatedContactInfo}
        productType={survey.productType}
      />
    )
  }
  setAnalyticsDimensions(
    participantId,
    result?.data?.participantAnalyticsIdentifiers,
    survey.productType,
    source,
  )

  let templates: { [key: string]: string } = {}
  if (participant.templates) {
    templates = JSON.parse(participant.templates)
    templates = Object.keys(templates).reduce(
      (acc, key) => ({ ...acc, [key]: t(templates[key] as TranslationKey) }),
      {},
    )
  }

  // Convert responses array to object {code: response}
  const responses: { [key: string]: string } = {}

  if (participant.responses) {
    participant.responses.forEach(r => {
      if (r === null) return
      responses[r.code] = r.response || ''
    })
  }

  return (
    <SurveyRouter
      surveySubmitted={participant.surveySubmitted}
      participantIsExpired={participant.isExpired}
      surveyType={survey.type}
      surveyProductType={survey.productType}
      hasConfidentialResults={survey.hasConfidentialResults}
      participantId={participantId}
      questions={participant.questions as PublicQuestions}
      levelOfCare={participant.levelOfCare as LOCTypeEnum}
      templates={templates}
      responses={responses}
      welcomeMessage={survey.welcomeMessage}
      onlineReviewSites={participant.location?.onlineReviewSites}
      isOpenLinkSurvey={survey.isOpenLinkSurvey}
      languages={survey.languages}
      translations={survey.translations}
      transitionTexts={survey.transitionTexts}
      source={source}
    />
  )
}

export default SurveyContainer
